import React, {useEffect, useRef, useState} from 'react';
import {Box, Typography} from '@mui/material';
import Loading from '../../components/Loading/Loading';
import {Panel} from '../../components/Panel/Panel';
import {useContactVulnerabilities} from '../../features/vulnerabilities/useContactVulnerabilities';
import {VulnerabilityOptionLists} from '../../features/vulnerabilities/useVulnerabilityOptions';
import {newDraftId} from '../../features/vulnerabilities/vulnerabilityFormatting';
import validateUUID from '../../utils/validateUUID';
import {ExistingVulnerabilitiesTable} from './ExistingVulnerabilitiesTable';
import {NewVulnerabilitiesTable, NewVulnerabilityRow} from './NewVulnerabilitiesTable';
import {SectionPayload} from './useReviewVulnerabilityDrafts';

export type SectionContact = {id: string; simplifiedName: string | null};

type Props = {
  contact: SectionContact;
  reviewId: string;
  options: VulnerabilityOptionLists;
  color?: string;
  readOnly?: boolean;
  /** Registers this section's payload builder with the page-level save. */
  register: (contactId: string, getPayload: SectionPayload | null) => void;
  /** Called after any edit so the page can schedule an autosave. */
  onChange: () => void;
};

/**
 * One contact's part of the SFP Vulnerability tube stop: existing rows from
 * Curo with a "Continuing?" toggle, and rows added in this review.
 */
export const ContactVulnerabilitySection: React.FC<Props> = ({
  contact,
  reviewId,
  options,
  color,
  readOnly = false,
  register,
  onChange,
}) => {
  const {existing, drafts, loading} = useContactVulnerabilities(contact.id, {
    reviewId,
    curoSync: true,
  });
  // Rows with an end date are already over; they are not shown in the review.
  const activeRows = existing.filter((row) => !row.endDate);
  const [continuing, setContinuing] = useState<Record<string, boolean>>({});
  const [newRows, setNewRows] = useState<NewVulnerabilityRow[]>([]);
  const initialised = useRef(false);

  // Seed the editable state from the server once, when the query first answers.
  useEffect(() => {
    if (loading || initialised.current) {
      return;
    }
    initialised.current = true;
    setContinuing(
      Object.fromEntries(
        existing.map((row) => [row.id, row.pendingEndReview?.id !== reviewId])
      )
    );
    setNewRows(
      drafts.map((draft) => ({
        id: draft.id,
        vulnerabilityType: draft.vulnerabilityType ?? '',
        serviceLevel: draft.serviceLevel ?? '',
      }))
    );
  }, [loading, existing, drafts, reviewId]);

  useEffect(() => {
    register(contact.id, () => ({
      contactId: validateUUID(contact.id),
      newRows: newRows.map((row) => ({
        id: validateUUID(row.id),
        vulnerabilityType: row.vulnerabilityType || null,
        serviceLevel: row.serviceLevel || null,
      })),
      endingVulnerabilityIds: activeRows
        .filter((row) => continuing[row.id] === false)
        .map((row) => row.id),
    }));
    return () => register(contact.id, null);
  }, [register, contact.id, newRows, continuing, activeRows]);

  const contactName = contact.simplifiedName || 'Contact';

  return (
    <Panel title={contactName} color={color}>
      {loading ? (
        <Loading entityName="vulnerabilities" />
      ) : (
        <Box display="flex" flexDirection="column" gap={3}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Existing Vulnerabilities
            </Typography>
            <ExistingVulnerabilitiesTable
              rows={activeRows}
              continuing={continuing}
              readOnly={readOnly}
              onContinuingChange={(rowId, value) => {
                setContinuing((current) => ({...current, [rowId]: value}));
                onChange();
              }}
            />
          </Box>
          <NewVulnerabilitiesTable
            rows={newRows}
            options={options}
            readOnly={readOnly}
            onAdd={() => {
              setNewRows((current) => [
                ...current,
                {id: newDraftId(), vulnerabilityType: '', serviceLevel: ''},
              ]);
              onChange();
            }}
            onChange={(rowId, patch) => {
              setNewRows((current) =>
                current.map((row) => (row.id === rowId ? {...row, ...patch} : row))
              );
              onChange();
            }}
            onRemove={(rowId) => {
              setNewRows((current) => current.filter((row) => row.id !== rowId));
              onChange();
            }}
          />
        </Box>
      )}
    </Panel>
  );
};
